'use es6';

import { useMemo } from 'react';
import { List, Map as ImmutableMap, fromJS } from 'immutable';
import I18n from 'I18n';
import { DISTINCT_APPROX, SUM, MIN } from 'reporting-data/constants/metricTypes';
import makeDateRangeByType from 'reporting-data/lib/makeDateRangeByType';
import { BASE_REPORT_CONFIG, DEAL_AMOUNT_TRANSFORMED_METRIC, DEAL_ID_TRANSFORMED_METRIC, CONTACT_ID_TRANSFORMED_METRIC, SUM_OP, DATASET_TYPE, DISTINCT_OP } from '../utils/constants';
import MetricLabel from '../components/revenueAttribution/MetricLabel';
import { getLearnMoreUrl } from '../utils/metricsTooltips';
import { jsx as _jsx } from "react/jsx-runtime";
const langKey = key => `components.revenueAttributionCard.${key}`;
export const limitedAccessHidePropertiesOnDrilldown = ['hs_touchpoint_type', 'hs_touchpoint_positions'];
export const limitedAccessHidePropertiesOnExport = [...limitedAccessHidePropertiesOnDrilldown, 'hs_linear_amount', 'hs_full_path_amount', 'hs_u_amount', 'hs_w_amount', 'hs_first_touch_amount', 'hs_last_touch_amount'];
const getConfigWithDealMetric = (report, attributionModel) => {
  return report.setIn(['config', 'dimensions'], fromJS(['hs_deal_id'])).updateIn(['config', 'metrics'], metricList => metricList.insert(2, fromJS({
    property: 'hs_deal_amount',
    metricTypes: [MIN]
  }))).setIn(['displayParams', 'showTotals'], true).set('visualization', fromJS({
    type: 'DATAWELL',
    layer: {
      dataset: 'primary',
      direction: 'horizontal',
      transform: [{
        type: 'AGGREGATE',
        aggregations: [{
          as: `${SUM}|${attributionModel}`,
          op: SUM_OP,
          field: `${SUM}|${attributionModel}`
        }, {
          as: DEAL_ID_TRANSFORMED_METRIC,
          op: SUM_OP,
          field: DEAL_ID_TRANSFORMED_METRIC
        }, {
          as: DEAL_AMOUNT_TRANSFORMED_METRIC,
          op: SUM_OP,
          field: DEAL_AMOUNT_TRANSFORMED_METRIC
        }, {
          as: CONTACT_ID_TRANSFORMED_METRIC,
          op: DISTINCT_OP,
          field: CONTACT_ID_TRANSFORMED_METRIC
        }]
      }],
      fieldOptions: [{
        field: `${SUM}|${attributionModel}`,
        dataset: DATASET_TYPE.PRIMARY
      }, {
        field: DEAL_ID_TRANSFORMED_METRIC,
        dataset: DATASET_TYPE.PRIMARY
      }, {
        field: DEAL_AMOUNT_TRANSFORMED_METRIC,
        dataset: DATASET_TYPE.PRIMARY
      }, {
        field: CONTACT_ID_TRANSFORMED_METRIC,
        dataset: DATASET_TYPE.PRIMARY
      }],
      valueFormattingOptions: []
    }
  }));
};
export default function useReportConfig({
  filters,
  attributionModel,
  dateRange,
  hasRevenueAttributionLimitedAccess,
  learnMoreUrls,
  screen
}) {
  return useMemo(() => {
    const dateRangeToJS = dateRange ? ImmutableMap.isMap(dateRange) ? dateRange.toJS() : dateRange : {};
    const reportConfig = BASE_REPORT_CONFIG.mergeIn(['config'], {
      filters: {
        custom: filters,
        dateRange: {
          property: 'hs_deal_close_date',
          value: makeDateRangeByType(dateRangeToJS)
        }
      },
      metrics: [{
        metricTypes: [SUM],
        property: attributionModel
      }, {
        metricTypes: [DISTINCT_APPROX],
        property: 'hs_deal_id'
      }, {
        property: 'hs_contact_id',
        metricTypes: []
      }]
    }).setIn(['displayParams', 'render', 'header'], rawMetric => {
      let metric;
      if (rawMetric.has('property')) {
        metric = rawMetric.get('property');
      } else {
        const field = rawMetric.get('field');
        if (field.includes('|')) {
          metric = field.split('|')[1];
        } else {
          metric = field;
        }
      }
      const learnMoreUrl = getLearnMoreUrl({
        learnMoreUrls,
        metric
      });
      return /*#__PURE__*/_jsx(MetricLabel, {
        metric: metric,
        hasRevenueAttributionLimitedAccess: hasRevenueAttributionLimitedAccess,
        learnMoreUrl: learnMoreUrl,
        screen: screen
      });
    }).setIn(['displayParams', 'hidePropertiesOnDrilldown'], hasRevenueAttributionLimitedAccess ? List(limitedAccessHidePropertiesOnDrilldown) : List()).setIn(['displayParams', 'hidePropertiesOnExport'], hasRevenueAttributionLimitedAccess ? List(limitedAccessHidePropertiesOnExport) : List()).set('name', I18n.text(langKey(hasRevenueAttributionLimitedAccess ? 'limitedAccess.title' : 'title')));
    return getConfigWithDealMetric(reportConfig, attributionModel);
  }, [filters, dateRange, attributionModel, hasRevenueAttributionLimitedAccess, learnMoreUrls, screen]);
}